/**
 * Minimum major versions shown in the outdated-browser banner.
 * Other Chromium browsers (Opera, Samsung Internet, Brave, Vivaldi, and so on)
 * are compared against the Google Chrome minimum, using the Chromium version
 * in the user agent.
 */
const BROWSER_SUPPORT = {
  chrome: { name: 'Google Chrome', label: 'Chrome', minMajor: 110 },
  edge: { name: 'Microsoft Edge', label: 'Edge', minMajor: 110 },
  safari: { name: 'Safari', label: 'Safari', minMajor: 15 },
  firefox: { name: 'Firefox', label: 'Firefox', minMajor: 147 },
} as const;

export interface DetectedBrowser {
  name: string;
  /** Short name used in the login notice, matching the design copy ("Chrome", "Edge"). */
  label: string;
  supportedVersion: number;
  userVersion: number;
}

function majorVersion(userAgent: string, pattern: RegExp): number | null {
  const match = pattern.exec(userAgent);
  if (!match?.[1]) return null;
  return Number.parseInt(match[1], 10);
}

function detected(browser: { name: string; label: string; minMajor: number }, userVersion: number): DetectedBrowser {
  return {
    name: browser.name,
    label: browser.label,
    supportedVersion: browser.minMajor,
    userVersion,
  };
}

/**
 * Identifies the browser family and major version from a user agent string.
 * Returns null when the browser is unrecognized or the version cannot be read.
 * Comparison uses the major version because that is how support is defined,
 * and Chromium freezes the remaining version digits in the user agent.
 */
export function detectBrowser(userAgent: string): DetectedBrowser | null {
  // Chrome, Edge, and Firefox on iOS are WebKit, not their desktop engines.
  // iOS 15 ships Safari 15, so the OS major version is the Safari version.
  if (/(?:CriOS|EdgiOS|FxiOS)\//.test(userAgent)) {
    const safariVersion = majorVersion(userAgent, /Version\/(\d+)/) ?? majorVersion(userAgent, /OS (\d+)[_.]/);
    if (safariVersion === null) return null;
    return detected(BROWSER_SUPPORT.safari, safariVersion);
  }

  // Edge UAs also contain a Chrome token. Chromium Edge uses Edg/; legacy EdgeHTML uses Edge/.
  const chromiumEdge = majorVersion(userAgent, /EdgA?\/(\d+)/);
  if (chromiumEdge !== null) {
    return detected(BROWSER_SUPPORT.edge, chromiumEdge);
  }

  const legacyEdge = majorVersion(userAgent, /Edge\/(\d+)/);
  if (legacyEdge !== null) {
    return detected(BROWSER_SUPPORT.edge, legacyEdge);
  }

  const firefox = majorVersion(userAgent, /Firefox\/(\d+)/);
  if (firefox !== null) {
    return detected(BROWSER_SUPPORT.firefox, firefox);
  }

  // Desktop Chrome and every other Chromium browser share the Chrome minimum.
  const chrome = majorVersion(userAgent, /(?:Chrome|Chromium)\/(\d+)/);
  if (chrome !== null) {
    return detected(BROWSER_SUPPORT.chrome, chrome);
  }

  // Desktop and iOS Safari. The Version token is the Safari release; the Safari token is WebKit.
  const isSafari = /Safari/i.test(userAgent) && !/Android/i.test(userAgent);
  if (isSafari) {
    const safari = majorVersion(userAgent, /Version\/(\d+)/);
    if (safari !== null) {
      return detected(BROWSER_SUPPORT.safari, safari);
    }
  }

  return null;
}

export function getOutdatedBrowser(userAgent: string): DetectedBrowser | null {
  const browser = detectBrowser(userAgent);
  if (!browser || browser.userVersion >= browser.supportedVersion) return null;
  return browser;
}

export function outdatedBrowserMessage(browser: DetectedBrowser): string {
  return `Your version of ${browser.label} (${browser.userVersion}) is no longer supported. We recommend updating to the latest version of Chrome for the best Air Reading experience.`;
}
